/**
 * Pure helpers that turn a Coach insight's strategy data into the compact
 * progression panel shown on the Dojo Coach card.
 *
 * Structured `strategy_context` is the source of truth; the prose parser only
 * exists so legacy insights (written before the envelope carried weeks) still
 * render a real timeline instead of a guess.
 */

const STATUS_PRESENTATION = {
  on_track: { key: 'on_track', icon: '✅', label: 'On track', className: 'on-track' },
  needs_attention: { key: 'needs_attention', icon: '⚠️', label: 'Needs attention', className: 'needs-attention' },
  needs_revision: { key: 'needs_revision', icon: '🔄', label: 'Needs revision', className: 'needs-revision' },
};

const UNKNOWN_STATUS = { key: 'unknown', icon: '•', label: 'Status unknown', className: 'unknown' };

const WEEK_PAIR_PATTERN = /week\s+(\d+)\s+of\s+(\d+)/i;
const VERDICT_TAIL_PATTERN = /(?:^|[\s—–-])(?:on track|needs attention|needs revision)\s*[.!]?\s*$/i;

const toPositiveNumber = value => {
  if (typeof value === 'boolean' || value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const trimmedText = value => (typeof value === 'string' && value.trim() ? value.trim() : null);

export const getStrategyStatusPresentation = verdict => STATUS_PRESENTATION[verdict] || UNKNOWN_STATUS;

export const parseWeekProgressFromSummary = summary => {
  const text = trimmedText(summary);
  const match = text ? text.match(WEEK_PAIR_PATTERN) : null;
  if (!match) return null;
  const currentWeek = toPositiveNumber(match[1]);
  const timelineWeeks = toPositiveNumber(match[2]);
  if (currentWeek === null || timelineWeeks === null) return null;
  return { currentWeek, timelineWeeks };
};

export const toSafeProgressPercent = (currentWeek, timelineWeeks) => {
  const current = toPositiveNumber(currentWeek);
  const total = toPositiveNumber(timelineWeeks);
  if (current === null || total === null) return null;
  return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
};

// `hasKnownVerdict` defaults to true so callers who only have a summary string
// (no verdict context) keep the historical behaviour of stripping verdict prose.
// The status pill only speaks for a *known* structured verdict, so when the
// verdict is unknown the same prose is the only place that information lives
// and must be kept rather than silently dropped.
export const extractProgressionDetail = (summary, hasKnownVerdict = true) => {
  const text = trimmedText(summary);
  if (!text) return null;
  const withoutWeek = text.replace(WEEK_PAIR_PATTERN, '').replace(/^\s*[—–:,-]\s*/, '').trim();
  const withoutVerdict = hasKnownVerdict ? withoutWeek.replace(VERDICT_TAIL_PATTERN, '').trim() : withoutWeek;
  return withoutVerdict || null;
};

export const buildCoachProgression = insight => {
  if (!insight) return null;
  const assessment = insight.strategy_assessment || null;
  const statusSummary = trimmedText(assessment?.status_summary);
  const context = insight.strategy_context || null;

  const structured = {
    currentWeek: toPositiveNumber(context?.current_week),
    timelineWeeks: toPositiveNumber(context?.timeline_weeks),
  };
  const weeks = (structured.currentWeek !== null && structured.timelineWeeks !== null)
    ? structured
    : parseWeekProgressFromSummary(statusSummary);

  const verdict = typeof assessment?.strategy_verdict === 'string' ? assessment.strategy_verdict : null;
  const hasKnownVerdict = Boolean(STATUS_PRESENTATION[verdict]);
  if (!weeks && !statusSummary && !hasKnownVerdict) return null;

  return {
    currentWeek: weeks?.currentWeek ?? null,
    timelineWeeks: weeks?.timelineWeeks ?? null,
    weekLabel: weeks ? `Week ${weeks.currentWeek} of ${weeks.timelineWeeks}` : null,
    percent: toSafeProgressPercent(weeks?.currentWeek, weeks?.timelineWeeks),
    detail: extractProgressionDetail(statusSummary, hasKnownVerdict),
    statusSummary,
    status: getStrategyStatusPresentation(verdict),
  };
};
